import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';

import {
  Checkbox,
  CheckboxTreeGroup,
  ValueType,
  Flex,
  ReorderIcon,
  Box,
  Divider,
} from '@frontend/legacy-components';

import { ItemAndDropBorderContainer } from 'components/BetweenItemDropBorder';
import {
  CUSTOM_DRAGGABLE_ITEM_TYPES,
  useDrag,
} from 'components/customDragLayer';

import { BetweenVisibleColumnDropBorder } from './BetweenVisibleColumnDropBorder';
import { ColumnState } from './types';

type ParentRowProps = {
  column: ColumnState;
  onVisibilityChange: (visibilityObj: ValueType) => void;
};

const ParentRow = ({ column, onVisibilityChange }: ParentRowProps) => {
  // translate to checkbox tree state if there are children
  // NOTE: assumes only one level deep of columns (workflow steps and metadata)
  const childValues = useMemo(() => {
    const out: ValueType = {};
    for (const childColumn of column.children ?? []) {
      out[childColumn.queryParamName] = !!childColumn.isVisible;
    }
    return out;
  }, [column]);

  const handleCheckboxTreeChange = useCallback(
    (nextValue: ValueType) => {
      onVisibilityChange(nextValue);
    },
    [onVisibilityChange],
  );

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onVisibilityChange({ [column.queryParamName]: event.target.checked });
    },
    [onVisibilityChange, column.queryParamName],
  );

  const hasChildren = column.children && column.children.length > 0;

  return hasChildren ? (
    <Box marginTop="1px">
      <CheckboxTreeGroup
        value={childValues}
        onChange={handleCheckboxTreeChange}
        key={column.queryParamName}
        parent={column.name}
        name={column.queryParamName}
      >
        {column.children?.map((subColumn) => (
          <Checkbox
            name={subColumn.queryParamName}
            key={subColumn.queryParamName}
          >
            {subColumn.name}
          </Checkbox>
        ))}
      </CheckboxTreeGroup>
    </Box>
  ) : (
    <Checkbox onChange={handleCheckboxChange} isChecked={!!column.isVisible}>
      {column.name}
    </Checkbox>
  );
};

type ParentRowDraggableProps = ParentRowProps & {
  index: number;
  onReorder: (oldPosition: number, nextPosition: number) => void;
  isLast: boolean;
};

const ParentRowDraggable = ({
  column,
  index,
  onVisibilityChange,
  onReorder,
  isLast,
}: ParentRowDraggableProps) => {
  const [{ isDragging }, connectDragSource, connectDragPreview] = useDrag({
    item: {
      draggableItemType: CUSTOM_DRAGGABLE_ITEM_TYPES.VIEWS_VISIBLE_COLUMNS,
      description: column.name,
      visibleColumns: [
        {
          queryParamName: column.queryParamName,
          position: index,
        },
      ],
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    connectDragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [connectDragPreview]);

  return (
    <ItemAndDropBorderContainer>
      <BetweenVisibleColumnDropBorder
        side="top"
        queryParamName={column.queryParamName}
        position={index}
        onReorderVisibleColumns={onReorder}
      />
      <Flex opacity={isDragging ? 0.3 : 1} alignItems="flex-start">
        {connectDragSource ? (
          <Box as="span" ref={connectDragSource}>
            <ReorderIcon
              marginRight={1}
              h="100%"
              w={3}
              color="feature.secondary"
              cursor={'grab'}
            />
          </Box>
        ) : null}
        <ParentRow column={column} onVisibilityChange={onVisibilityChange} />
      </Flex>
      {isLast && (
        <BetweenVisibleColumnDropBorder
          side="bottom"
          queryParamName={column.queryParamName}
          position={index + 1}
          onReorderVisibleColumns={onReorder}
        />
      )}
    </ItemAndDropBorderContainer>
  );
};

type ColumnConfigProps = {
  visibleColumnsState: ColumnState[];
  setVisibleColumnsState: React.Dispatch<React.SetStateAction<ColumnState[]>>;
};

export const VisibleColumnsConfig = ({
  visibleColumnsState,
  setVisibleColumnsState,
}: ColumnConfigProps) => {
  const { fixedColumns, freeColumns } = useMemo(() => {
    const fixedColumns: ColumnState[] = [];
    const freeColumns: ColumnState[] = [];
    for (const topLevelColumn of visibleColumnsState) {
      if (topLevelColumn.isFixed) {
        fixedColumns.push(topLevelColumn);
      } else {
        freeColumns.push(topLevelColumn);
      }
    }
    return { fixedColumns, freeColumns };
  }, [visibleColumnsState]);

  const handleVisibilityChange = useCallback(
    (visibilityObj: ValueType) => {
      // NOTE: assumes children are only one level deep at most
      const next: ColumnState[] = JSON.parse(JSON.stringify(freeColumns));
      for (const nextColumn of next) {
        if (nextColumn.queryParamName in visibilityObj) {
          nextColumn.isVisible = !!visibilityObj[nextColumn.queryParamName];
        }
        for (const childColumn of nextColumn.children ?? []) {
          if (childColumn.queryParamName in visibilityObj) {
            childColumn.isVisible = !!visibilityObj[childColumn.queryParamName];
          }
        }
      }
      const nextFixed = JSON.parse(JSON.stringify(fixedColumns));
      for (const nextColumn of nextFixed) {
        if (nextColumn.queryParamName in visibilityObj) {
          nextColumn.isVisible = !!visibilityObj[nextColumn.queryParamName];
        }
      }
      setVisibleColumnsState(nextFixed.concat(next));
    },
    [fixedColumns, freeColumns, setVisibleColumnsState],
  );

  const handleReorder = useCallback(
    (oldPosition: number, nextPosition: number) => {
      const next: ColumnState[] = JSON.parse(JSON.stringify(freeColumns));
      // Temporarily store the item to be moved
      const itemToMove = next[oldPosition];
      // Move the item to the next position
      next.splice(oldPosition, 1); // Remove item from current position
      // Insert item at next position
      if (oldPosition > nextPosition) {
        next.splice(nextPosition, 0, itemToMove);
      } else {
        next.splice(nextPosition - 1, 0, itemToMove);
      }
      const nextFixed = JSON.parse(JSON.stringify(fixedColumns));
      setVisibleColumnsState(nextFixed.concat(next));
    },
    [setVisibleColumnsState, fixedColumns, freeColumns],
  );

  return (
    <Flex gap={1} direction="column" width="100%">
      {fixedColumns.map((column) => (
        <ParentRow
          key={column.queryParamName}
          column={column}
          onVisibilityChange={handleVisibilityChange}
        />
      ))}
      {fixedColumns.length > 0 && <Divider />}
      {freeColumns.map((column, index) => (
        <ParentRowDraggable
          key={column.queryParamName}
          column={column}
          index={index}
          onVisibilityChange={handleVisibilityChange}
          onReorder={handleReorder}
          isLast={index === freeColumns.length - 1}
        />
      ))}
    </Flex>
  );
};
